import { BenefitsViewController } from './interfaces';
import { useBikletaPointsService } from '../../../services/bikletaPoints/bikletaPointsService';
import { useEffect, useState } from 'react';
import { useStore } from '../../../utils/useStore';
import { BenefitsType } from '../../../services/bikletaPoints/dto';
import { useAsync } from '../../../utils/useAsync';
import { useToast } from '@chakra-ui/react';

export const useBenefitsViewController = (): BenefitsViewController => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const toast = useToast();

  const bpService = useBikletaPointsService();

  const { activePoint, setActivePoint } = useStore();

  const [data, error, loading, execute, refreshing, refresh] = useAsync(
    bpService.getBenefits,
    {
      docs: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalDocuments: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
    }
  );

  useEffect(() => {
    if (activePoint) {
      execute(activePoint._id, { page, limit });
    }
  }, [activePoint, page, limit]);

  const submitBenefitData = async (data: BenefitsType) => {
    try {
      setIsSubmitting(true);
      const res = await bpService.createBenefit(activePoint._id, data);

      setActivePoint(res);
    } catch (error) {
      console.error(error);
      throw new Error('error at submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateBenefit = async (data: BenefitsType) => {
    try {
      setIsSubmitting(true);
      const res = await bpService.updateBenefit(activePoint._id, data);

      setActivePoint(res);
    } catch (error) {
      console.error(error);
      throw new Error('error at update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteBenefits = async (benefitsToDelete: any[]) => {
    const mappedBenefits = benefitsToDelete.map(item => item.values._id);
    try {
      setIsDeleting(true);
      await Promise.all(
        mappedBenefits.map(benefit => bpService.deleteBenefit(benefit))
      );

      toast({
        title: 'Beneficios Borrados',
        description: 'Se eliminaron los beneficios de tu Bikleta Point.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });

      refresh(activePoint._id, { page, limit });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Ocurrió un error',
        description: 'Intentelo nuevamente.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsDeleting(false);
    }

    return;
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  };

  return {
    submitBenefitData,
    deleteBenefits,
    updateBenefit,
    isDeleting,
    isSubmitting,
    benefitsData: data?.docs || [],
    pagination: data?.pagination || {
      currentPage: 1,
      totalPages: 0,
      totalDocuments: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
    onPageChange,
    onLimitChange,
  };
};
